import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../api";
import { useAppStoreSearch } from "@/hooks/useAppStore";
import { AppSearchInput } from "./AppSearchInput";
import { AppSearchResults } from "./AppSearchResults";
import { SelectedAppPreview } from "./SelectedAppPreview";

interface CreateAppDialogProps {
  onClose: () => void;
  onAppCreated?: () => void;
}

export const CreateAppDialog = ({ onClose, onAppCreated }: CreateAppDialogProps) => {
  const [appSearchTerm, setAppSearchTerm] = useState("");
  const [selectedApp, setSelectedApp] = useState<any>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const { data: searchResults, isLoading: isSearching } = useAppStoreSearch(appSearchTerm);
  const categoriesData = useQuery(api.appCategories.list.categories);
  const addApp = useMutation(api.apps.add.app);
  const addCategory = useMutation(api.appCategories.add.category);

  // Transform Convex categories data
  const categories = categoriesData ? 
    (Array.isArray(categoriesData) ? categoriesData : []).map((category: any) => ({
      _id: category._id || category.id || "",
      name: category.name || "",
      tags: Array.isArray(category.tags) ? category.tags : [],
    })) : [];

  // Update selected category when app is selected
  useEffect(() => {
    if (selectedApp && selectedApp.category) {
      setSelectedCategory(selectedApp.category);
      // Clear selected tags when category changes
      setSelectedTagIds([]);
    }
  }, [selectedApp]);

  const handleAppSelect = (app: any) => {
    setSelectedApp(app);
    setAppSearchTerm(app.appName);
  };

  const handleSearchChange = (value: string) => {
    setAppSearchTerm(value);
    setSelectedApp(null);
    setSelectedCategory("");
    setSelectedTagIds([]);
  };

  const handleCategoryChange = (newCategory: string) => {
    setSelectedCategory(newCategory);
    // Clear selected tags when category changes
    setSelectedTagIds([]);
  };

  const handleTagsUpdate = (newTagIds: string[]) => {
    setSelectedTagIds(newTagIds);
  };

  const ensureCategoryExists = async (categoryName: string) => {
    // Check if category already exists
    const existingCategory = categories.find(cat => cat.name.toLowerCase() === categoryName.toLowerCase());
    if (existingCategory) {
      return categoryName;
    }

    // Category doesn't exist, create it
    try {
      await addCategory({
        name: categoryName,
        tags: [],
      });
      
      toast({
        title: "Category Created",
        description: `Created new category: ${categoryName}`,
      });
      
      return categoryName;
    } catch (error) {
      console.error('Error creating category:', error);
      toast({
        title: "Warning",
        description: `Could not create category ${categoryName}. Using as-is.`,
        variant: "destructive",
      });
      return categoryName;
    }
  };

  const handleCreateApp = async () => {
    if (!selectedApp) {
      toast({
        title: "Error",
        description: "Please select an app from the search results",
        variant: "destructive",
      });
      return;
    }

    if (!selectedCategory) {
      toast({
        title: "Error",
        description: "Please select a category",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      // Ensure the category exists before creating the app
      const finalCategory = await ensureCategoryExists(selectedCategory);
      
      await addApp({
        name: selectedApp.appName,
        bundleId: selectedApp.bundleId,
        iconUrl: selectedApp.iconUrl || "",
        category: finalCategory,
        trackId: selectedApp.trackId ? selectedApp.trackId.toString() : "",
        tags: selectedTagIds,
      });
      
      toast({
        title: "Success",
        description: "App added successfully",
      });
      
      // Trigger refresh of the parent component
      if (onAppCreated) {
        onAppCreated();
      }
      
      onClose();
    } catch (error) {
      console.error('Error creating app:', error);
      toast({
        title: "Error",
        description: "Failed to add app",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DialogContent className="sm:max-w-[425px]">
      <DialogHeader>
        <DialogTitle>Add New App</DialogTitle>
      </DialogHeader>
      <div className="grid gap-4 py-4">
        <AppSearchInput
          searchQuery={appSearchTerm}
          onSearchChange={handleSearchChange}
          onCancel={() => {
            setAppSearchTerm("");
            setSelectedApp(null);
            setSelectedCategory("");
            setSelectedTagIds([]);
          }}
          placeholder="Search App Store..."
        />
        
        {/* Search Results */}
        {searchResults && searchResults.length > 0 && !selectedApp && (
          <AppSearchResults
            searchQuery={appSearchTerm}
            apps={searchResults}
            onAppSelect={handleAppSelect}
            selectedApps={selectedApp ? [selectedApp] : []}
          />
        )}
        
        {/* Selected App Preview */}
        {selectedApp && (
          <SelectedAppPreview selectedApp={selectedApp} />
        )}
        
        {/* Category Selection */}
        {selectedApp && (
          <div className="grid gap-2">
            <Label>Category</Label>
            <Select value={selectedCategory} onValueChange={handleCategoryChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {/* Show App Store category as first option if it's different from existing categories */}
                {selectedApp.category && !categories.some(cat => cat.name.toLowerCase() === selectedApp.category.toLowerCase()) && (
                  <SelectItem value={selectedApp.category}>
                    {selectedApp.category} (from App Store)
                  </SelectItem>
                )}
                {categories.map((category) => (
                  <SelectItem key={category._id} value={category.name}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        
        {/* Tags Selection */}
        {selectedApp && selectedCategory && (
          <div className="grid gap-2">
            <Label>Tags (Optional)</Label>
            <p className="text-sm text-gray-500">
              Available tags: {categories.find(c => c.name === selectedCategory)?.tags?.join(", ") || "None"}
            </p>
          </div>
        )}
      </div>
      
      <div className="flex justify-end space-x-2">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button 
          onClick={handleCreateApp}
          disabled={!selectedApp || !selectedCategory || isLoading}
        >
          {isLoading ? "Adding..." : "Add App"}
        </Button>
      </div>
    </DialogContent>
  );
};
